import http from '@/http'
import i18n from '@/locales'
import router from '@/router'
import columns from './columns'
import cartSuggestions from './cartSuggestions'

import {
  BOM_RECEIVED,
  BOM_ILLUSTRATION_RECEIVED,
  BOM_ITEMS_SELECT,
  BOM_ITEMS_SELECT_ARRAY,
  BOM_LOADING,
  BOM_UPDATE_LAYOUT,
  CLEAR_BOM,
  CLEAR_BOM_ITEMS_SELECT,
  UPDATE_BOM_ALLOW_MULTISELECT
} from '../contentTypes'
import { entityTypePluralize } from '../contentHelpers'

const state = getDefaultState()

const getters = {
  allowMultiSelect: (state) => state.allowMultiSelect
}

let bomSearchRequestId = 0

const actions = {
  async getBom ({ commit, dispatch }, { id, type }) {
    try {
      await dispatch('getBomColumns')
      if (type === 'page') {
        if (!state.illustration || id !== state.illustration.id) {
          commit(BOM_LOADING)

          const entityTypePlural = entityTypePluralize(type)
          const { data } = await http.get(`${entityTypePlural}/${id}/parts`)
          await dispatch('mapBomItems', { bom: data })
        }
      }
    } catch (err) {
      console.log('err :>> ', err)
      const response = err.response
      router.replace({ path: '/error', query: { status: response.status } }).catch(() => {})
    }
  },
  // A copy of above, to respect the different callers and their respective privileges
  async getAdminBom ({ commit, dispatch, state }, { id, type }) {
    try {
      await dispatch('getBomColumns')
      if (type === 'page') {
        /*
         * Content must be committed to the store prior to invocation
         * Otherwise, the if-check below will fail
         */
        if (!state.illustration || id !== state.illustration.id) {
          commit(BOM_LOADING)

          const entityTypePlural = entityTypePluralize(type)
          const { data } = await http.get(`admin/${entityTypePlural}/${id}/parts`)
          await dispatch('mapBomItems', { bom: data })
        }
      }
    } catch (err) {
      console.log('err :>> ', err)
      const response = err.response
      router.replace({ path: '/error', query: { status: response.status } }).catch(() => {})
    }
  },
  selectBomItems ({ commit, state }, { item, append }) {
    let found = false
    if (state.selected && state.selected.length) {
      found = state.selected.find((selected) => selected === item)
    }

    if (append) {
      // Toggle this item while preserving others
      if (!found) {
        commit(BOM_ITEMS_SELECT, { item, append })
      } else {
        commit(BOM_ITEMS_SELECT, { item: '', append })
      }
    } else {
      // Replace selection with just this item
      commit(BOM_ITEMS_SELECT, { item })
    }
  },
  selectBomItemsFromCallout ({ commit }, item) {
    const items = state.itemsMap[item]

    if (items && items.length) {
      commit(BOM_ITEMS_SELECT, { item: items[0] })
    } else {
      commit(BOM_ITEMS_SELECT, { item: '' })
    }
  },
  selectBomItemsFromCalloutArray ({ commit, state }, calloutItems) {
    const foundItems = calloutItems
      .flatMap(item => {
        const items = state.itemsMap[item]
        return items?.length ? items[0] : []
      })

    if (foundItems.length) {
      commit(BOM_ITEMS_SELECT_ARRAY, { items: foundItems })
    } else {
      commit(CLEAR_BOM_ITEMS_SELECT)
    }
  },
  clearBomItemSelection ({ commit }) {
    commit(CLEAR_BOM_ITEMS_SELECT)
  },
  updateBomLayout ({ commit }, bomLayout) {
    const layout = bomLayout || (state.layout === 'horizontal' ? 'vertical' : 'horizontal')
    commit(BOM_UPDATE_LAYOUT, { layout })
  },
  setAllowMultiSelect ({ commit }, allowMultiSelect) {
    commit(UPDATE_BOM_ALLOW_MULTISELECT, allowMultiSelect)
  },
  async bomSearchWithReturn ({ state, rootState }, { query }) {
    const requestId = ++bomSearchRequestId
    try {
      const trimmedQuery = query.trim()
      if (trimmedQuery) {
        const length = Object.keys(state.itemsMap).length
        var newQuery
        if (!trimmedQuery.startsWith("\"")) {
          newQuery = '(' + trimmedQuery + ') OR (' + trimmedQuery + '*)'
        } else {
          newQuery = trimmedQuery
        }
        // Offset not supported by the endpoint
        const params = {
          limit: length || 1000,
          fk_page_id: rootState.content.id,
          q: newQuery
        }
        const { data } = await http.get('bom-search', { params })
        if (requestId !== bomSearchRequestId) return []
        return data.items
      }
    } catch (err) {
      // na
    }
    return []
  },
  async mapBomItems ({ commit }, { bom }) {
    let map = {}
    for (const bomItem of bom) {
      const index = bom.indexOf(bomItem);
      if (!map[bomItem.itemText]) {
        map[bomItem.itemText] = []
      }
      bomItem.popoverPositionBottom = (bom.length < 14) ? true : index < 14
      bomItem.showHotpointLinkPopover = false
      bomItem.showThumbnailPopover = false
      // calculating position based on existence of other popover items.
      // ordering is important here as thumbnail is always first in line of icons on UX
      // for the other thumbnails, the same prescription can be applied using the ordering sequence below
      let count = 0
      if(bomItem.commentCount > 0) count += 1
      if(bomItem.relatedCount > 0) count += 1
      if(bomItem.tagCount > 0) count += 1
      bomItem.thumbnailPopoverPosition = count += 1
      bomItem.index = index
      map[bomItem.itemText].push(bomItem)
    }
    commit(BOM_RECEIVED, { map: map, bom: bom })
  }
}

const mutations = {
  [BOM_RECEIVED] (state, { map, bom }) {
    state.items = bom
    state.selected = []
    state.itemsMap = map
    state.isLoaded = true
    state.allowMultiSelect = false
  },
  [CLEAR_BOM_ITEMS_SELECT] (state) {
    state.selected = []
  },
  [BOM_ITEMS_SELECT] (state, { item, append = false }) {
    if (!item) {
      // If no item provided, clear selection
      state.selected = []
      return
    }

    if (append) {
      // In append mode, add or remove this item while preserving others
      if (item.itemText === i18n.global.t('itemNotShown') || !item.itemText) {
        state.selected = state.selected.filter(i => i !== item)
      } else {
        const itemsToToggle = state.itemsMap[item.itemText] || []
        // Remove items if they exist, add them if they don't
        const existingItems = state.selected.filter(i => itemsToToggle.includes(i))
        if (existingItems.length > 0) {
          state.selected = state.selected.filter(i => !itemsToToggle.includes(i))
        } else {
          state.selected = [...state.selected, ...itemsToToggle]
        }
      }
    } else {
      // In replace mode, set selection to just this item
      if (item.itemText === i18n.global.t('itemNotShown') || !item.itemText) {
        state.selected = [item]
      } else {
        state.selected = state.itemsMap[item.itemText] || []
      }
    }
    if (!append && Array.isArray(state.selected) && state.selected.length > 1) {
      //we need to find the element of the two or more selected items
      //that is the truly currently selected item
      const selectedIdx = state.selected.findIndex((it) => {
        if (it === item) return true
        if (it?.pagePartId != null && item?.pagePartId != null) {
          return String(it.pagePartId) === String(item.pagePartId)
        }
        if (it?.instanceId != null && item?.instanceId != null) {
          return String(it.instanceId) === String(item.instanceId)
        }
        return false
      })
      if (selectedIdx > 0) {
        //reorder the selected array to put that item first if it isn't
        const selectedItems = state.selected.slice()
        const selectedItem = selectedItems.splice(selectedIdx, 1)[0]
        state.selected = [selectedItem, ...selectedItems]
      }
    }

    // Only scroll if we're not in append mode
    if (!append) {
      // Scroll to Element
      const element = document.getElementById(item.pagePartId)
      if (element) {
        const parent = element.closest('.bomWrapper')
        if (parent) {
          parent.scrollTo({
            top: element.offsetTop - (parent.clientHeight / 2),
            behavior: 'smooth'
          })
        }
      }
    }
  },
  [BOM_ITEMS_SELECT_ARRAY] (state, { items }) {
    if (!items || !items.length) {
      state.selected = []
      return
    }
    state.selected = items.flatMap(i => {
      if (i.itemText === i18n.global.t('itemNotShown') || !i.itemText) {
        return [i]
      } else {
        return state.itemsMap[i.itemText] || []
      }
    })
  },
  [BOM_ILLUSTRATION_RECEIVED] (state, { illustration }) {
    state.illustration = illustration ? {
      id: illustration.id || '',
      name: illustration.name || '',
      type: illustration.type || '',
      contentType: illustration.contentType || ''
    } : null;
  },
  [BOM_UPDATE_LAYOUT] (state, { layout }) {
    state.layout = layout;
  },
  [BOM_LOADING] (state) {
    state.isLoaded = false
    state.selected = []
  },
  [CLEAR_BOM] (state) {
    Object.assign(state, getDefaultState())
  },
  [UPDATE_BOM_ALLOW_MULTISELECT] (state, allowMultiSelect) {
    state.allowMultiSelect = allowMultiSelect
  }
}

export default {
  state,
  actions,
  getters,
  mutations,
  modules: {
    columns,
    cartSuggestions
  }
}

export function getDefaultState() {
  return {
    illustration: undefined,
    isLoaded: false,
    items: [],
    itemsMap: {},
    layout: 'horizontal',
    selected: undefined,
    allowMultiSelect: false
  }
}
